<template>
  <Panel
    class="social-reposts-modal custom-scrollbar position-relative"
    :variant="['transparent']"
  >
    <span
      class="position-absolute mx-4 px-3 close-icon cursor-hand"
      @click="closeModal"
    >
      <Icon name="close" width="16" :fill="$scss.primaryTextBg" />
    </span>
    <Title
      class="text-center"
      :text="
        $t('_web_account_my-link-social-reposts-title', 'Link your accounts')
      "
    />
    <TextBlock class="text-center my-4" variant="secondary" tag="p">{{
      $t(
        '_web_account_my-link-social-reposts-description',
        'Linking accounts allows you automatically share your posts and tournaments news to linked Social Media'
      )
    }}</TextBlock>
    <CollapsiblePanel
      label="Facebook"
      :variant="['dark-bg', 'rounded']"
      :visible="!twitterVisible && facebookVisible"
      @change="facebookToggled"
    >
      <template #labelSuffix>
        <div
          v-if="hasFacebookConnection"
          class="h-ml-auto d-flex align-items-center"
        >
          <span class="text-success">{{
            $t('_web_account_my-link-social-linked-label', 'Linked')
          }}</span>
          <Icon
            name="checked_2"
            class="h-ml-3"
            width="18"
            :fill="$scss.greenBg"
          />
        </div>
      </template>
      <div class="row action-wrapper d-flex justify-content-between">
        <div v-if="hasFacebookConnection" class="col d-flex">
          <TextBlock variant="secondary" tag="p">{{
            $t(
              '_web_account_my-link-social-fb-connected',
              'You already connected your Facebook account with Playhera. Enjoy the automatic reposts feature! If you no longer interested in this connection, delete your credentials with the next button.'
            )
          }}</TextBlock>
          <div class="px-3">&nbsp;</div>
          <HeraFormBtn
            :label="
              $t('_web_account_my-link-social-fb-delete-button', 'Unlink')
            "
            class="btn-secondary-simple flex-shrink-0"
            :disabled="facebookLoading"
            @click="deleteFacebookCredentials"
          />
        </div>
        <div v-else class="col">
          <p>
            <TextBlock
              tag="a"
              href="https://developers.facebook.com/docs/apps/"
              target="_blank"
              variant="accent"
              >{{
                $t('_web_account_my-link-social-fb-manual-link', 'How to link?')
              }}</TextBlock
            >
          </p>
          <ol class="h-pl-4">
            <li>
              <div class="row">
                <div class="col-12 col-md-6">
                  <TextBlock tag="p">{{
                    $t(
                      '_web_account_my-link-social-facebook-step1-info',
                      'Provide your Facebook application Id, please'
                    )
                  }}</TextBlock>
                </div>
                <div class="col-12 col-md-6">
                  <TextInput
                    v-model="facebookData.facebookClientId"
                    :placeholder="
                      $t(
                        '_web_account_my-link-social-fb-client-id-label',
                        'Client ID'
                      )
                    "
                    class="mb-4"
                    required
                  />
                </div>
              </div>
            </li>
            <li class="mt-4">
              <div class="row">
                <div class="col-12 col-md-6">
                  <TextBlock tag="p">{{
                    $t(
                      '_web_account_my-link-social-facebook-step2-info',
                      'Provide your Facebook application token, please'
                    )
                  }}</TextBlock>
                </div>
                <div class="col-12 col-md-6">
                  <TextArea
                    v-model="facebookData.facebookAccessToken"
                    :placeholder="
                      $t(
                        '_web_account_my-link-social-fb-token-label',
                        'Access token'
                      )
                    "
                    required
                    :rows="3"
                    class="mb-4"
                  />
                </div>
              </div>
            </li>
          </ol>

          <div class="text-center">
            <HeraFormBtn
              :label="
                $t('_web_account_my-link-social-fb-submit-button', 'Submit')
              "
              class="btn-secondary-simple"
              :disabled="facebookLoading || !hasValidFacebookCredentials"
              @click="saveFacebookCredentials"
            />
          </div>
        </div>
      </div>
    </CollapsiblePanel>
    <CollapsiblePanel
      class="mt-5"
      label="Twitter"
      :variant="['dark-bg', 'rounded']"
      :visible="!facebookVisible && twitterVisible"
      @change="twitterToggled"
    >
      <template #labelSuffix>
        <div
          v-if="hasTwitterConnection"
          class="h-ml-auto d-flex align-items-center"
        >
          <span class="text-success">{{
            $t('_web_account_my-link-social-linked-label', 'Linked')
          }}</span>
          <Icon
            name="checked_2"
            class="h-ml-3"
            width="18"
            :fill="$scss.greenBg"
          />
        </div>
      </template>
      <div
        v-if="hasTwitterConnection"
        class="row action-wrapper d-flex justify-content-between align-items-end no-gutters"
      >
        <div class="col d-flex">
          <TextBlock variant="secondary" tag="p">{{
            $t(
              '_web_account_my-link-social-twitter-connected',
              'You already connected your Twitter account with Playhera. Enjoy the automatic reposts feature! If you no longer interested in this connection, delete your credentials with the next button.'
            )
          }}</TextBlock>
          <div class="px-3">&nbsp;</div>
          <HeraFormBtn
            :label="
              $t('_web_account_my-link-social-twitter-delete-button', 'Unlink')
            "
            class="btn-secondary-simple flex-shrink-0"
            :disabled="twitterLoading"
            @click="deleteTwitterCredentials"
          />
        </div>
      </div>
      <template v-else>
        <ol class="h-pl-4">
          <li class="mt-4">
            <div class="row">
              <div class="col-12 col-md-6">
                <TextBlock tag="p">{{
                  $t(
                    '_web_account_my-link-social-twitter-step1-description',
                    'Authorize a Playhera app to receive a secure pin from Twitter'
                  )
                }}</TextBlock>
              </div>
              <p class="col-12 col-md-5 h-ml-auto">
                <HeraFormBtn
                  :label="
                    $t(
                      '_web_account_my-link-social-twitter-auth-button',
                      'Authorize'
                    )
                  "
                  class="btn-secondary-simple w-100"
                  @click="getTwitterTokens"
                />
              </p>
            </div>
          </li>
          <li class="mt-4">
            <div class="row">
              <div class="col-12 col-md-6">
                <TextBlock tag="p">{{
                  $t(
                    '_web_account_my-link-social-twitter-step2-description',
                    'Verify pin, received from Twitter'
                  )
                }}</TextBlock>
              </div>
              <TextInput
                v-model="twitterData.twitterVerifierCode"
                required
                :disabled="waitForTwitterPin || twitterLoading"
                :placeholder="
                  $t(
                    '_web_account_my-link-social-twitter-pin-label',
                    'Pin code'
                  )
                "
                class="col-12 col-md-5 h-ml-auto"
              />
            </div>
          </li>
        </ol>
        <div class="text-center">
          <HeraFormBtn
            :label="
              $t('_web_account_my-link-social-twitter-submit-button', 'Submit')
            "
            :disabled="twitterLoading || !hasValidTwitterCredentials"
            class="h-ml-3 btn-secondary-simple"
            @click="submitTwitterTokens"
          />
        </div>
      </template>
    </CollapsiblePanel>
  </Panel>
</template>
<script>
import { mapActions, mapState, mapMutations } from 'vuex';
import Title from '@/components/atoms/Title';
import Panel from '@/components/atoms/Panel';
import CollapsiblePanel from '@/components/atoms/CollapsiblePanel';
import TextBlock from '@/components/atoms/TextBlock';
import TextInput from '@/components/atoms/form/HeraFormTextInput';
import TextArea from '@/components/molecules/form/TextInputMultiline';
import HeraFormBtn from '@/components/atoms/HeraFormBtn';
import Icon from '@/components/atoms/Icon';
export default {
  name: 'LinkSocialModal',
  components: {
    Title,
    Panel,
    CollapsiblePanel,
    TextBlock,
    TextInput,
    TextArea,
    HeraFormBtn,
    Icon,
  },
  data() {
    return {
      facebookData: {
        facebookClientId: '',
        facebookAccessToken: '',
      },
      facebookLoading: true,
      facebookVisible: false,
      twitterData: {
        twitterRequestToken: '',
        twitterVerifierCode: '',
      },
      twitterLoading: true,
      waitForTwitterPin: true,
      twitterVisible: false,
    };
  },
  computed: {
    ...mapState('my', {
      account: 'account',
      myAccountId: 'accountId',
    }),
    hasValidFacebookCredentials() {
      return Boolean(
        this.facebookData.facebookClientId &&
          this.facebookData.facebookAccessToken
      );
    },
    hasValidTwitterCredentials() {
      return Boolean(
        this.twitterData.twitterRequestToken &&
          this.twitterData.twitterVerifierCode
      );
    },
    hasFacebookConnection: {
      get() {
        return this.$store.state.accounts.my.socialConnections
          .facebookCredentialsDefined;
      },
      set(newVal) {
        this.updateAccountSocialConnections({
          facebookCredentialsDefined: newVal,
        });
      },
    },
    hasTwitterConnection: {
      get() {
        return this.$store.state.accounts.my.socialConnections
          .twitterCredentialsDefined;
      },
      set(newVal) {
        this.updateAccountSocialConnections({
          twitterCredentialsDefined: newVal,
        });
      },
    },
  },
  created() {
    this.fetchAccountSocialConnectionStatus()
      .catch(this.errorNotify)
      .finally(() => {
        this.facebookLoading = false;
        this.twitterLoading = false;
      });
  },
  methods: {
    ...mapActions([
      'closeModal',
      'fetchAccountSocialConnectionStatus',
      'setAccountSocialConnection',
      'deleteSocialConnection',
      'getTwitterToken',
      'errorNotify',
    ]),
    ...mapMutations('my', ['updateAccountSocialConnections']),
    ...mapMutations('current', ['updateAccountModel']),
    ...mapMutations(['updateEditData']),
    saveFacebookCredentials() {
      if (this.hasValidFacebookCredentials) {
        this.facebookLoading = true;
        this.setAccountSocialConnection({
          connections: { ...this.facebookData },
        })
          .then(response => {
            this.hasFacebookConnection = true;
            this.facebookData = {};
            if (response) {
              this.updateAccountModel({
                social: {
                  ...this.account.social,
                  fb: response.socialProfileId,
                },
              });
              this.updateEditData({
                social: {
                  ...this.account.social,
                  fb: response.socialProfileId,
                },
              });
            }
          })
          .catch(this.errorNotify)
          .finally(() => {
            this.facebookLoading = false;
          });
      }
    },
    deleteFacebookCredentials() {
      this.facebookLoading = true;
      this.deleteSocialConnection({ platform: 'facebook' })
        .then(() => {
          this.hasFacebookConnection = false;
          this.updateAccountModel({
            social: { ...this.account.social, fb: '' },
          });
          this.updateEditData({ social: { ...this.account.social, fb: '' } });
        })
        .catch(this.errorNotify)
        .finally(() => {
          this.facebookLoading = false;
        });
    },
    facebookToggled(isVisible) {
      this.facebookVisible = isVisible;
      if (isVisible) {
        this.twitterVisible = false;
      }
    },
    getTwitterTokens() {
      this.getTwitterToken()
        .then(response => {
          if (response) {
            this.twitterData.twitterRequestToken = response.requestToken;
            this.waitForTwitterPin = false;
          }
        })
        .then(this.openTwitterPinPopup)
        .catch(this.errorNotify);
    },
    openTwitterPinPopup() {
      window.open(
        `https://api.twitter.com/oauth/authorize?oauth_token=${
          this.twitterData.twitterRequestToken
        }`,
        'PinfromTwitterForPlayhera',
        'resizable,scrollbars'
      );
    },
    submitTwitterTokens() {
      if (this.hasValidTwitterCredentials) {
        this.twitterLoading = true;
        this.setAccountSocialConnection({
          connections: { ...this.twitterData },
        })
          .then(response => {
            this.hasTwitterConnection = true;
            this.waitForTwitterPin = true;
            this.twitterData = {};
            if (response) {
              this.updateAccountModel({
                social: {
                  ...this.account.social,
                  twitter: response.socialProfileId,
                },
              });
              this.updateEditData({
                social: {
                  ...this.account.social,
                  twitter: response.socialProfileId,
                },
              });
            }
          })
          .catch(this.errorNotify)
          .finally(() => {
            this.twitterLoading = false;
          });
      }
    },
    deleteTwitterCredentials() {
      this.twitterLoading = true;
      this.deleteSocialConnection({ platform: 'twitter' })
        .then(() => {
          this.hasTwitterConnection = false;
          this.updateAccountModel({
            social: { ...this.account.social, twitter: '' },
          });
          this.updateEditData({
            social: { ...this.account.social, twitter: '' },
          });
        })
        .catch(this.errorNotify)
        .finally(() => {
          this.twitterLoading = false;
        });
    },
    twitterToggled(isVisible) {
      this.twitterVisible = isVisible;
      if (isVisible) {
        this.facebookVisible = false;
      }
    },
  },
};
</script>

<style lang="scss">
.social-reposts-modal {
  max-height: 100vh;
  max-width: 650px;
  width: 100%;
  .close-icon {
    @include auto-rtl(right, 0);
  }
}
</style>
